// ============================================================
// EWFL Awards & Team of the Week
// ============================================================
import { calculateStandings } from './gameData';

// Build the Team of the Week from all match ratings on a given week
// Returns 11 players (1 GK, 4 DEF, 3 MID, 3 ATT) with highest ratings
export const buildTeamOfTheWeek = (teams, fixtures, week, playerMatchResult) => {
  const matchesThisWeek = fixtures.filter(f => f.week === week && f.played);
  const candidates = []; // { playerId, teamId, position, rating, goals, assists }

  matchesThisWeek.forEach(fixture => {
    if (!fixture.matchRatings) return;
    Object.entries(fixture.matchRatings).forEach(([playerId, info]) => {
      const team = teams.find(t => t.players.some(p => p.id === playerId));
      const player = team?.players.find(p => p.id === playerId);
      if (!player) return;
      candidates.push({
        playerId, teamId: team.id, teamName: team.name,
        playerName: player.name, position: player.position, flag: player.flag,
        rating: info.rating || 6.5, goals: info.goals || 0, assists: info.assists || 0
      });
    });
  });

  // Also include the career player's match performance
  if (playerMatchResult && playerMatchResult.playerId) {
    candidates.push({
      playerId: playerMatchResult.playerId,
      teamId: playerMatchResult.teamId,
      teamName: playerMatchResult.teamName,
      playerName: playerMatchResult.playerName,
      position: playerMatchResult.position,
      flag: playerMatchResult.flag,
      rating: playerMatchResult.rating,
      goals: playerMatchResult.goals || 0,
      assists: playerMatchResult.assists || 0,
      isCareerPlayer: true
    });
  }

  const byPos = (filterFn, count) => candidates.filter(filterFn).sort((a, b) => b.rating - a.rating).slice(0, count);

  const gk = byPos(c => c.position === 'GK', 1);
  const def = byPos(c => ['CB','LB','RB','LWB','RWB','SW'].includes(c.position), 4);
  const mid = byPos(c => ['CDM','CM','CAM','LM','RM'].includes(c.position), 3);
  const att = byPos(c => ['LW','RW','ST','CF','SS','LF','RF'].includes(c.position), 3);

  return {
    week,
    players: [...gk, ...def, ...mid, ...att]
  };
};

// Compute end-of-season award leaderboards
// Returns: topScorers, topAssists, cleanSheets, POTY (Player of the Year by avg rating), standings
export const computeSeasonAwards = (teams, matchHistory, careerPlayerId) => {
  // Top scorers — across all player match logs (from teams.players stats.league)
  const allPlayers = teams.flatMap(t => t.players.map(p => ({ ...p, teamName: t.name, teamId: t.id })));
  const sorted = (key) => [...allPlayers]
    .filter(p => (p.stats?.league?.[key] || 0) > 0)
    .sort((a, b) => (b.stats?.league?.[key] || 0) - (a.stats?.league?.[key] || 0))
    .slice(0, 10);

  const topScorers = sorted('goals');
  const topAssists = sorted('assists');

  // Clean sheets — defensive team-based, but also GK
  const goalkeepers = allPlayers.filter(p => p.position === 'GK');
  const cleanSheets = [...goalkeepers]
    .filter(gk => (gk.stats?.league?.cleanSheets || 0) > 0)
    .sort((a, b) => (b.stats?.league?.cleanSheets || 0) - (a.stats?.league?.cleanSheets || 0))
    .slice(0, 10);

  // POTY — highest avg rating among players with ≥10 appearances
  // Approximate via goals+assists contribution or use match history for career player
  // For NPCs use a combined score
  const poty = [...allPlayers]
    .filter(p => (p.stats?.league?.matchesPlayed || 0) >= 15)
    .map(p => ({
      ...p,
      potyScore:
        (p.stats?.league?.goals || 0) * 3 +
        (p.stats?.league?.assists || 0) * 2 +
        (p.stats?.league?.cleanSheets || 0) * 1.5 +
        (p.overall || 70) * 0.15
    }))
    .sort((a, b) => b.potyScore - a.potyScore)
    .slice(0, 10);

  const standings = calculateStandings(teams);

  // Check if career player appears in each
  const careerInScorers = topScorers.findIndex(p => p.id === careerPlayerId);
  const careerInAssists = topAssists.findIndex(p => p.id === careerPlayerId);
  const careerInCleanSheets = cleanSheets.findIndex(p => p.id === careerPlayerId);
  const careerInPOTY = poty.findIndex(p => p.id === careerPlayerId);

  return {
    topScorers,
    topAssists,
    cleanSheets,
    poty,
    standings,
    careerInScorers,
    careerInAssists,
    careerInCleanSheets,
    careerInPOTY
  };
};
